// HelpPage — in-app documentation of how PointsPilot works, what it
// can and can't do, and how to get the most out of it. Honest about
// limits + what's deferred so users have realistic expectations.

export default function HelpPage() {
  return (
    <div className="max-w-3xl mx-auto px-3 sm:px-4 pb-20">
      <div className="text-center my-6">
        <div className="text-brand-ink/55 text-xs font-bold uppercase tracking-[0.3em]">HELP & GUIDE</div>
        <h1 className="text-2xl sm:text-3xl font-black mt-1 text-brand-ink">How PointsPilot works</h1>
        <p className="text-sm text-brand-ink/70 mt-1 max-w-lg mx-auto">
          What it does, what it doesn't, and the gotchas to know before booking.
        </p>
      </div>

      <Section title="🤖 What Pilot does" tone="teal">
        <p>Pilot is the AI trip planner inside PointsPilot. It plans your entire trip end-to-end:</p>
        <ul>
          <li><strong>Days per city</strong> with reasoning based on your vibe</li>
          <li><strong>Per-leg flight picks</strong> from live seats.aero award data, ranked by cents-per-mile</li>
          <li><strong>Hotels per stop</strong> with neighborhood + walkability fit</li>
          <li><strong>Activities + restaurants</strong> matched to your vibe + dietary constraints</li>
          <li><strong>Day-by-day time-slotted schedule</strong> when budget permits</li>
          <li><strong>Reasoning per pick</strong> + alternatives you can swap in with one tap</li>
          <li><strong>Step-by-step booking guides</strong> for every flight + hotel</li>
        </ul>
      </Section>

      <Section title="⚠️ What Pilot does NOT do" tone="amber">
        <p><strong>Pilot is a PLANNER, not a BOOKER.</strong> It surfaces booking links + phone numbers but does not actually book anything for you. You execute every booking yourself on the official airline / hotel sites.</p>
        <p>This is intentional — award bookings need final verification before transferring miles, and transfers are irreversible. Pilot guides you to the booking; you pull the trigger.</p>
      </Section>

      <Section title="✈️ Multi-airport search" tone="teal">
        <p>On Pilot's Step 1 you can add multiple home airports (JFK + EWR + LGA for NYC, LAX + BUR + LGB + SNA + ONT for LA, etc.). Pilot searches award space at every airport in parallel and picks the cheapest one for outbound + return.</p>
        <p><strong>Tip:</strong> Often saves 20-30K miles per traveler — EWR frequently has more Aeroplan space than JFK on the same date.</p>
      </Section>

      <Section title="📚 Trip versions + comparison" tone="teal">
        <p>Save the current plan as a named version ("Foodie focus", "Budget mode"). Compare any 2 versions side-by-side with cost breakdown, stops, hotels, flights, activities, restaurants, and day-by-day schedule.</p>
        <p><strong>Mix-and-match:</strong> In compare view, each hotel has "→ Use in [other version]" — copy picks across versions to build a hybrid.</p>
      </Section>

      <Section title="💬 Chat with Pilot" tone="teal">
        <p>After the plan generates, chat with Pilot to refine. Type, push-to-talk (🎤), or upload a screenshot (📎). Pilot identifies what's in the image + researches it via web search + compares to your plan.</p>
        <p><strong>Lock picks</strong> (🔒 button on every card) before asking Pilot to "make it cheaper" — locked picks survive replans untouched.</p>
        <p><strong>Chat history saves</strong> to the backend, syncs across devices, survives cache clears.</p>
      </Section>

      <Section title="📋 Booking step-by-step" tone="teal">
        <p>Every flight + hotel card has a <strong>"📋 Step-by-step booking guide"</strong> expander with 5 steps from transfer-miles → verify-availability → book → enter-passenger-info → save-confirmation. Walks first-time award bookers through the whole flow.</p>
      </Section>

      <Section title="🎁 Transfer bonuses" tone="teal">
        <p>Active transfer bonuses from the DB are surfaced as a banner at the top of every plan. Pilot factors them into flight picks ("Bilt → Aeroplan +100% bonus means 35K Bilt buys 70K Aeroplan miles — that's the move").</p>
      </Section>

      <Section title="🔴 Known limits + workarounds" tone="rose">
        <ul>
          <li>
            <strong>Catalog is Lisbon + Porto only right now.</strong> Other destinations have basic catalog data but no per-city tagging — multi-city for non-Portugal destinations may mix catalog rows across sub-cities. Will expand catalog over time.
          </li>
          <li>
            <strong>Cash equivalent numbers are estimates</strong> (based on industry cpp tables), not live Skyscanner data. The "saves $3,100" might be ±20%. Verify cash fare before claiming the saving.
          </li>
          <li>
            <strong>Award alerts don't auto-check yet.</strong> Tap "Check now" on each alert manually. Background polling + push notifications coming.
          </li>
          <li>
            <strong>Pilot's first generation can take 30-60s</strong> (Claude does deep reasoning + web search + per-leg seats.aero queries). The wizard shows elapsed time + a cancel button at 60s. 90s hard timeout aborts the request automatically.
          </li>
          <li>
            <strong>HEIC photos from iPhone</strong> work on iOS Safari (native decode), may fail on Chrome desktop — save as JPEG first if you hit the error.
          </li>
          <li>
            <strong>Only one "active" trip at a time</strong> — planning a new destination overwrites the previous active trip's working state. Past trips still browsable in My Trip → Past Trips section. Save as a version before switching to preserve.
          </li>
        </ul>
      </Section>

      <Section title="🔵 Coming later" tone="brand">
        <ul>
          <li>Live cash fare backup (Skyscanner / Kiwi integration)</li>
          <li>Real-time bank-points sync (Plaid)</li>
          <li>Push notifications for award alerts</li>
          <li>Award price trend tracking over time</li>
          <li>Position flight auto-suggester</li>
          <li>Hotel room category guide (Cat 5/6/7/8 per Hyatt property)</li>
        </ul>
      </Section>

      <Section title="🛟 Troubleshooting" tone="brand">
        <ul>
          <li><strong>Pilot generation stalls past 60s:</strong> Tap "Cancel & try again." If it happens repeatedly, check that ANTHROPIC_API_KEY + SEATS_AERO_API_KEY are set in Railway env vars.</li>
          <li><strong>"No live award found" on all legs:</strong> Either SEATS_AERO_API_KEY is missing, OR the dates have no biz class availability in that window (try ±2 weeks).</li>
          <li><strong>Hotel/flight catalog is empty after deploy:</strong> Visit the admin page (/api/admin/pilot on the backend URL) and tap the green re-run button to populate enrichment.</li>
          <li><strong>Chat history vanished:</strong> Should sync from backend on chat open. If empty after refresh, the backend may be missing the pilot_chats column — re-run admin migrations.</li>
        </ul>
      </Section>
    </div>
  );
}

function Section({ title, tone, children }) {
  const toneCls = {
    teal: 'border-teal/30 bg-teal/5',
    amber: 'border-amber-500/30 bg-amber-500/5',
    rose: 'border-rose-500/30 bg-rose-500/5',
    brand: 'border-brand-ink/15 bg-navy-light',
  }[tone] || 'border-brand-ink/15 bg-navy-light';
  return (
    <div className={`border-2 rounded-2xl p-4 mb-3 ${toneCls}`}>
      <h2 className="text-base font-black text-brand-ink mb-2">{title}</h2>
      <div className="text-sm text-brand-ink/85 space-y-2 leading-relaxed [&_ul]:list-disc [&_ul]:ml-5 [&_ul]:space-y-1 [&_li]:text-[13px] [&_strong]:text-brand-ink">
        {children}
      </div>
    </div>
  );
}
